// Pure SVG export functions — no React state, no side effects.

import { createElement } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import type { Box, VisibleLevels, Arrow, Area, Department } from '../types/index';
import { FlowchartSvgScene, computeSvgDimensions } from './FlowchartSvgScene';
import type { FlowchartSVGOptions } from './FlowchartSvgScene';
import { is24hEndFromStart, convertDurationUnit, assignBoxesToAreas, buildExpandedGanttRows, workDaysBetween, calcAreaCompletion } from './gantt';

export type { FlowchartSVGOptions };

export interface GanttSVGOptions {
  ganttTasks: Array<Omit<Box, 'duration'> & { start: number; duration: number }>;
  sortedTaskIds: number[];
  durationUnit: string;
  visibleLevels: VisibleLevels;
  ganttNonWorkingDays: string[];
  /** Configured work days of the week (0=Sun...6=Sat), default Mon-Fri. Named ganttWorkDays
   *  (not workDays) to avoid colliding with this file's own internal display-day-list var. */
  ganttWorkDays?: number[];
  ganttStartDate: Date;
  fullDaySchedule: boolean;
  /** Workday start hour (0-23), default 9. Ignored when fullDaySchedule is on. */
  dayStartHour?: number;
  /** Workday length in hours, default 8. Ignored when fullDaySchedule is on. */
  hoursPerDay?: number;
  workHoursOnly: boolean;
  showAllGanttDeps: boolean;
  arrows: Arrow[];
  /** On-screen zoom level (same factor used by the live Gantt view) — fallback when screenDw isn't available. */
  ganttZoom?: number;
  /** On-screen title column width — keeps the exported label column consistent with what the user sees. */
  labelWidth?: number;
  /** Already-computed on-screen pixel-per-column value (live view's `dw`). When provided, used directly so the export matches the live view's zoom exactly instead of being re-derived from a different column count. */
  screenDw?: number;
  /** "Set timeline dates" range from the live view. When set, the export is cropped to this date range instead of the full project span. */
  timelineView?: { start: string; end: string } | null;
  /** When true (and timelineView is set), rows whose bar has no overlap with the timeline range are omitted entirely instead of just having their bar clipped out of view. */
  onlyVisibleBars?: boolean;
  /** Display-only: show all 24 hours per day (hours mode) instead of only the configured Workday hours. Default true (current/compressed behavior). Mirrors GanttPanel's own toggle. */
  hideNonWorkingHours?: boolean;
  /** Real clock start of every alwaysOn/is24_5 task — required to position their bars correctly when hideNonWorkingHours is off (a chained is24h task's schedule column doesn't linearly map to real time). Same map GanttPanel builds via getAlwaysOnClockStart. */
  is24hRealStarts?: Map<number, Date>;
  /** Gantt-only bar duration-label display unit (hours/days/weeks) — independent of
   *  durationUnit (scheduling). Defaults to durationUnit when not provided. */
  ganttDurationDisplay?: string;
  /** "Filtered Export": when set (non-null), only these top-level box ids are
   *  included — every other row is dropped entirely, same as onlyVisibleBars but
   *  driven by an explicit per-row pick instead of timeline overlap. Null/undefined
   *  means no filtering (export everything, current behavior). */
  selectedBoxIds?: number[] | null;
  /** Each subitem's own scheduled position (lib/gantt.ts's calcSubitemGanttTasks,
   *  same array GanttPanel gets from useGanttSchedule.ts) — already shifted onto
   *  its parent's absolute start in this same raw, ganttStartDate-relative
   *  coordinate space `ganttTasks` itself uses, so (unlike GanttPanel's live view,
   *  which has its own calendarOrigin-relative "adjusted" space) no re-anchoring is
   *  needed here. Only rendered when includeSubitemsInExport is true. */
  subitemGanttTasks?: Array<Omit<Box, 'duration'> & { start: number; duration: number; parentBoxId?: number }>;
  /** All boxes (top-level and subitems) and areas, for Area-toplevel grouping via
   *  lib/gantt.ts's assignBoxesToAreas — only used when includeAreaGroupsInExport
   *  is true. */
  boxes?: Box[];
  areas?: Area[];
  /** Mirror GanttPanel's own View-menu toggles, so the export matches whatever the
   *  live view currently shows instead of always being top-level-boxes-only. */
  includeSubitemsInExport?: boolean;
  includeAreaGroupsInExport?: boolean;
  includeRegularBoxesInExport?: boolean;
  /** "Filter by category" selection from the live view (GanttPanel's own
   *  categoryFilter) — department names to include; empty/omitted means no
   *  filtering. Was never wired into the export before, so a filtered-out task
   *  still showed up underneath whatever was actually visible on screen. Needs
   *  `departments` (below) to resolve a task's departmentColor back to a name,
   *  same lookup GanttPanel.tsx's own category filter uses. */
  categoryFilter?: string[];
  departments?: Department[];
  /** Each top-level task's own start/end display text (useGanttSchedule.ts's
   *  ganttStartEndText — same strings the canvas's start/end badges show). Used
   *  only when includeStartEndColumnInExport is true; a task with no entry (e.g.
   *  a subitem) renders an empty cell. */
  startEndText?: Map<number, { start: string; end: string }>;
  /** Mirrors GanttPanel's showStartEndColumnInGantt — appends a fixed-width column
   *  after the title with each task's start/end text. */
  includeStartEndColumnInExport?: boolean;
}

export function buildGanttSVGString(opts: GanttSVGOptions): { svg: string; W: number; H: number } | null {
  const {
    ganttTasks, sortedTaskIds, durationUnit, visibleLevels, ganttNonWorkingDays,
    ganttWorkDays: configuredWorkDays = [1, 2, 3, 4, 5],
    ganttStartDate, fullDaySchedule, workHoursOnly, showAllGanttDeps, arrows,
    ganttZoom = 1, labelWidth, screenDw, timelineView, onlyVisibleBars,
    dayStartHour = 9, hoursPerDay = 8, hideNonWorkingHours = true, is24hRealStarts,
    ganttDurationDisplay = durationUnit, selectedBoxIds,
    subitemGanttTasks = [], boxes = [], areas = [],
    includeSubitemsInExport = false, includeAreaGroupsInExport = false, includeRegularBoxesInExport = true,
    categoryFilter = [], departments = [],
    startEndText, includeStartEndColumnInExport = false,
  } = opts;

  // showAllHoursExport: mirrors GanttPanel's showAllHours — each day gets a full 24
  // display columns (instead of just hoursPerDay) so is24h tasks running through the
  // night have somewhere on the timeline to show that.
  const showAllHoursExport = durationUnit === 'hours' && !hideNonWorkingHours;

  const allCalcTasks = ganttTasks;
  // Same two-step filter as GanttPanel.tsx's own `tasks` (type exclusion, then
  // category) — this used to be skipped entirely here, so a category-filtered-out
  // task still showed up in the export underneath whatever was actually on screen.
  // A subitem's parent being filtered out here is enough to drop the subitem too
  // (getSubitemsFor below only ever looks up subitems of a task that's still present).
  const rawTasks = allCalcTasks
    .filter(t => t.type !== 'callout' && t.type !== 'startday')
    .filter(t => categoryFilter.length === 0 || categoryFilter.includes(departments.find(d => d.color === t.departmentColor)?.name || ''));
  if (rawTasks.length === 0) return null;

  // Use gantt order from ref (populated when Gantt view is open)
  const orderedIds = sortedTaskIds;
  const taskMap = new Map(rawTasks.map(t => [t.id, t]));
  const orderedSet = new Set(orderedIds);
  const orderedTasks: typeof rawTasks = [
    ...orderedIds.map(id => taskMap.get(id)).filter((t): t is typeof rawTasks[0] => !!t),
    ...rawTasks.filter(t => !orderedSet.has(t.id)),
  ];

  const maxEndRaw = Math.max(...orderedTasks.map(t => t.start + t.duration));
  const maxEnd = Math.ceil(Math.max(maxEndRaw, 1));

  // "Show start/end" columns (GanttPanel.tsx's showStartEndColumnInGantt) — two
  // fixed-width extra strips (Start, then End) appended after the title column,
  // same convention as the live view's own separate sibling cells. titleLabelW
  // (the ORIGINAL, unwidened label width) stays the basis for the title text's own
  // clip-path/truncation below — only LABEL_W (used everywhere else: bars, grid
  // lines, header bands) grows, so everything downstream shifts right to make room
  // without the title text encroaching on the new columns.
  const DATE_COL_W = 90;
  const titleLabelW = labelWidth ?? 220;
  const LABEL_W = titleLabelW + (includeStartEndColumnInExport ? DATE_COL_W * 2 : 0);
  const ROW_H = 28;
  const HDR_H = 20;
  const unit = durationUnit;

  // Re-generate calendar header bands (same logic as generateCalendarHeaders inside Gantt render)
  const nonWorkingSet = new Set(ganttNonWorkingDays);
  const toDateStr = (d: Date) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  // NOTE: GanttPanel.tsx's calendarOrigin shifts earlier than ganttStartDate when a
  // scheduledStart pin elsewhere in the project predates it — this file previously
  // tried mirroring that by shifting startDate the same way, but that was wrong: on
  // GanttPanel's side, EVERY task's display position is independently re-derived via
  // getAdjStart (a full calendar-based walk of the dependency graph relative to
  // calendarOrigin) rather than decoding calcSchedule's raw task.start directly —
  // task.start there is never fed through colList as-is. This file has no equivalent
  // re-derivation: toDisplayPos here decodes task.start (always relative to
  // ganttStartDate, calcSchedule's own origin) directly via schedDayList, so
  // schedDayList must stay anchored at ganttStartDate to remain internally
  // consistent — shifting it to calendarOrigin without also re-deriving task.start
  // desynced the two, making regular-task and is24h positioning measurably worse
  // (confirmed live: a task that should show "22 Oct 1:30" moved further wrong, to
  // "21 Oct 22:00", after this shift was tried). Reverted; the calendarOrigin
  // divergence in the rare early-pin case remains a known, unresolved gap between
  // this file and the live view until a proper equivalent of getAdjStart exists here.
  const startDate = new Date(ganttStartDate); startDate.setHours(0, 0, 0, 0);
  const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const dayNames = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  const hPerDayExport = fullDaySchedule ? 24 : hoursPerDay;
  // displayHoursPerDay: per-day column width for DISPLAY-space math (header bands, view
  // window, dw). Stays equal to hPerDayExport unless showAllHoursExport expands it to 24.
  const displayHoursPerDay = showAllHoursExport ? 24 : hPerDayExport;

  // Column position of an arbitrary date, via pure date arithmetic — already in display
  // space (counts every calendar day when workHoursOnly is off, exactly like workDays
  // below), so no further conversion needed for view-window math.
  //
  // Can legitimately be negative: a "Set Timeline Dates" range (or a saved export-row
  // preset's own range, see GanttPanel.tsx) can start before ganttStartDate — most
  // commonly when ganttStartDate is floating to "today" (gantt_start_date is null) and
  // the flowchart has scheduledStart-pinned tasks dated in the past, which calcSchedule
  // explicitly supports (see calcStart's own comment). Previously this clamped to 0
  // (Math.max(0, ...)) whenever the target preceded startDate — silently collapsing a
  // past range onto "today" instead, which is a COMPLETELY different window, not a
  // reasonable fallback. GanttPanel's own live-view equivalent never had this problem:
  // it extends its day-list backward to cover exactly this case (see displayOrigin) —
  // workDays below does the same now. Using the shared, already-bidirectional
  // workDaysBetween (lib/gantt.ts) here gets the same backward support for free, instead
  // of hand-rolling a forward-only loop.
  //
  // Defined early (before workDays/schedDayList) purely so workDays' own backward extent
  // can be sized before it's built — see backDisplayUnitsNeeded below. Deliberately only
  // ever used for that sizing (via viewStartColFromStartDate), never as the final,
  // rendered view-window position: it counts work days via raw date arithmetic
  // (workDaysBetween), which is off by exactly one work day from every OTHER
  // display-column computation in this file (all of which key off the workDays array's
  // own 0-based index) whenever startDate itself isn't a qualifying work day — a rough
  // over-estimate is fine for sizing a buffer, but not for where a bar/crop actually
  // lands. The final view window (viewStartCol/viewEndColRaw, below workDays) uses
  // dateStrToDisplayCol instead, which searches that same array directly and so can
  // never disagree with it. See dateStrToDisplayCol's own comment for how this was found.
  const dateToColBounded = (dateStr: string): number => {
    const [yr, mo, dy] = dateStr.split('T')[0].split('-').map(Number);
    const target = new Date(yr, mo - 1, dy, 12, 0, 0);
    if (unit === 'weeks') {
      const ganttNoon2 = new Date(startDate); ganttNoon2.setHours(12, 0, 0, 0);
      return Math.round((target.getTime() - ganttNoon2.getTime()) / (7 * 86400000));
    }
    const count = workDaysBetween(startDate, target, workHoursOnly, ganttNonWorkingDays, configuredWorkDays);
    return unit === 'hours' ? count * displayHoursPerDay : count;
  };
  const viewStartColFromStartDate = timelineView?.start ? dateToColBounded(timelineView.start) : 0;

  const unitsPerWorkDay = unit === 'hours' ? displayHoursPerDay : unit === 'days' ? 1 : 0.2;
  const unitsPerHour   = unit === 'hours' ? 1 : unit === 'days' ? 0.125 : 0.025;

  const esc = (s: string) => (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  // An is24h task's REAL calendar end can fall far later than what its schedule-column
  // value (task.start + task.duration, calcSchedule's abstract work-hour units) implies —
  // a 24h/24_5 chain packs many calendar days (nights, weekends) into comparatively few
  // schedule-column hours. Sizing schedDayList/workDays purely from maxEnd (schedule-column
  // space, below) can then fall short of the real calendar date an is24h task actually
  // needs: realDateToDisplayCol's search loop finds no matching (or later) entry and
  // silently falls back to the list's LAST entry, mispositioning the bar by however many
  // calendar days the list came up short — not a fixed amount, since it depends on how far
  // the schedule-column estimate has drifted from real time by that point in the chain
  // (this is what produced a different-looking offset on each check: it grows/shrinks as
  // upstream predecessors change). Fix: extend neededWorkDays to also cover the latest real
  // end among all is24h tasks, computed the same way their bars are (is24hEndFromStart).
  let maxIs24hCalendarDays = 0;
  if (is24hRealStarts) {
    // Subitems (inherited is24h from their parent — see lib/gantt.ts's
    // calcSubitemGanttTasks) can reach further than any top-level task alone;
    // orderedTasks is top-level only, so include subitemGanttTasks here too.
    for (const t of [...orderedTasks, ...subitemGanttTasks]) {
      if (!(t.alwaysOn || t.is24_5)) continue;
      const realStart = is24hRealStarts.get(t.id);
      if (!realStart) continue;
      const realEnd = is24hEndFromStart(realStart, t.duration ?? 0, t.is24_5 ?? false, ganttNonWorkingDays, configuredWorkDays);
      const days = Math.ceil((realEnd.getTime() - startDate.getTime()) / 86400000) + 1;
      if (days > maxIs24hCalendarDays) maxIs24hCalendarDays = days;
    }
  }

  // Two date lists, mirroring GanttPanel's colList (schedule space) vs displayColList
  // (display space) — they diverge whenever workHoursOnly is off, because then the display
  // list includes weekend columns that the schedule list (which calcSchedule always used to
  // compute task.start/duration) never had. schedDayList is ALWAYS filtered to weekdays +
  // non-working days, regardless of workHoursOnly, matching calcSchedule's own assumption;
  // workDays (display space) only filters when workHoursOnly is on.
  const neededWorkDays = Math.max(
    maxIs24hCalendarDays,
    unit === 'hours' ? Math.ceil(maxEnd / hPerDayExport) : unit === 'days' ? maxEnd : Math.ceil(maxEnd * 5),
  );

  // Both lists are built forward-only from startDate by default — fine as long as nothing
  // needed ever falls before it. Two things can: a task's own schedule position (a
  // scheduledStart pin dated before ganttStartDate — calcSchedule explicitly supports a
  // negative task.start for this, see calcStart's own comment) and the view window's own
  // start (viewStartCol above, now that dateToColBounded supports negative too). Previously
  // neither list extended backward at all, so toDisplayPos/toDisplayEndPos silently clamped
  // such a task to startDate's own column (schedDayList) regardless of its real, earlier
  // position, and the header bands (built by iterating workDays below) had nothing before
  // startDate to show even once the view window itself was correctly positioned to reveal
  // it. Walking backward first (own loop, reversed, prepended) and tracking how many
  // entries that added gets both lists however far back is actually needed, while leaving
  // the common (nothing-before-startDate) case untouched — schedBackOffset is 0 and every
  // formula below behaves exactly as it did before this existed.
  const minSchedCol = Math.min(0, ...orderedTasks.map(t => t.start));
  const backWorkDaysNeeded = unit === 'hours' ? Math.ceil(Math.max(0, -minSchedCol) / hPerDayExport)
    : unit === 'days' ? Math.ceil(Math.max(0, -minSchedCol)) : 0;

  const schedDayList: Date[] = [];
  if (backWorkDaysNeeded > 0) {
    const backEntries: Date[] = [];
    let calDayBack = -1;
    while (backEntries.length < backWorkDaysNeeded) {
      const cur = new Date(startDate); cur.setDate(startDate.getDate() + calDayBack--);
      const dow = cur.getDay();
      if (dow < 1 || dow > 5) continue;
      if (nonWorkingSet.has(toDateStr(cur))) continue;
      backEntries.push(cur);
    }
    schedDayList.push(...backEntries.reverse());
  }
  const schedBackOffset = schedDayList.length;
  {
    let calDay = 0;
    while (schedDayList.length < schedBackOffset + neededWorkDays) {
      const cur = new Date(startDate); cur.setDate(startDate.getDate() + calDay++);
      const dow = cur.getDay();
      if (dow < 1 || dow > 5) continue;
      if (nonWorkingSet.has(toDateStr(cur))) continue;
      schedDayList.push(cur);
    }
  }

  // workDays' own backward need is driven by viewStartCol (display space) rather than
  // minSchedCol (schedule space) — the view window can start earlier than every task, or
  // vice versa, independently.
  const backDisplayUnitsNeeded = Math.max(0, -viewStartColFromStartDate);
  const backCalendarDaysNeeded = unit === 'hours' ? Math.ceil(backDisplayUnitsNeeded / displayHoursPerDay)
    : unit === 'days' ? Math.ceil(backDisplayUnitsNeeded) : 0;

  const workDays: { date: Date; dow: number; isWeekday: boolean; isNonWorking: boolean }[] = [];
  if (backCalendarDaysNeeded > 0) {
    const backEntries: typeof workDays = [];
    let calDayBack = -1;
    while (backEntries.length < backCalendarDaysNeeded) {
      const cur = new Date(startDate); cur.setDate(startDate.getDate() + calDayBack--);
      const dow = cur.getDay(); const isWeekday = dow >= 1 && dow <= 5;
      if (workHoursOnly && !isWeekday) continue;
      const ds = toDateStr(cur); if (workHoursOnly && nonWorkingSet.has(ds)) continue;
      backEntries.push({ date: cur, dow, isWeekday, isNonWorking: nonWorkingSet.has(ds) });
    }
    workDays.push(...backEntries.reverse());
  }
  const workDaysBackCount = workDays.length;
  {
    let calDay = 0;
    while (workDays.length < workDaysBackCount + neededWorkDays) {
      const cur = new Date(startDate); cur.setDate(startDate.getDate() + calDay++);
      const dow = cur.getDay(); const isWeekday = dow >= 1 && dow <= 5;
      if (workHoursOnly && !isWeekday) continue;
      const ds = toDateStr(cur); if (workHoursOnly && nonWorkingSet.has(ds)) continue;
      workDays.push({ date: cur, dow, isWeekday, isNonWorking: nonWorkingSet.has(ds) });
    }
  }
  const dispDateIndex = new Map<string, number>(workDays.map((wd, i) => [toDateStr(wd.date), i]));

  // Finds an arbitrary date's display-column position by its actual index in
  // workDays (exact match, or the next-later qualifying day) — exactly mirroring
  // realDateToDisplayCol's own lookup further down. This replaces using
  // workDaysBetween(startDate, target) (via dateToColBounded) directly as the
  // FINAL view-window position: workDaysBetween's count and a plain 0-based array
  // index only agree when startDate itself is a qualifying work day. Whenever it
  // isn't (e.g. a weekend — routine since gantt_start_date commonly just gets
  // defaulted to "today"/the creation date, with no regard for which weekday that
  // lands on), workDaysBetween's count is off by exactly one work day relative to
  // every OTHER display-column computation in this file, which all key off this
  // same workDays array — confirmed live: a saved export-row preset's own range
  // (itself computed via toDisplayPos/getStartDate, i.e. array-index-based)
  // landed one work day before the view window dateToColBounded computed for the
  // identical date, leaving every bar positioned just outside the crop. Only used
  // for hours/days — weeks mode has no array to be inconsistent with (toDisplayPos
  // returns schedCol as-is for it), so it still goes through dateToColBounded.
  const dateStrToDisplayCol = (dateStr: string): number => {
    if (unit === 'weeks') return dateToColBounded(dateStr);
    const [datePart] = dateStr.split('T');
    const [yr, mo, dy] = datePart.split('-').map(Number);
    const dayNoon = new Date(yr, mo - 1, dy, 12, 0, 0);
    const ty = dayNoon.getFullYear(), tm = dayNoon.getMonth(), td = dayNoon.getDate();
    let di = workDays.length - 1;
    for (let i = 0; i < workDays.length; i++) {
      const d = workDays[i].date;
      if (d.getFullYear() === ty && d.getMonth() === tm && d.getDate() === td) { di = i; break; }
      const dn = new Date(d); dn.setHours(12, 0, 0, 0);
      if (dn > dayNoon) { di = i; break; }
    }
    return unit === 'hours' ? di * displayHoursPerDay : di;
  };
  const viewStartCol = timelineView?.start ? dateStrToDisplayCol(timelineView.start) : 0;

  // Convert a task's schedule-space position (task.start / task.start+duration, always
  // computed by calcSchedule as if only weekdays exist) into display-space (the column grid
  // actually rendered here, which spreads out over weekends too when workHoursOnly is off).
  // Without this, bars are positioned as if every display column were a work day, badly
  // misaligning them against the real-date-based header/view-window math below whenever
  // workHoursOnly is off. No-op for weeks mode. Mirrors GanttPanel's toDisplayPos exactly.
  //
  // rawWdi/rawDi (can be negative) is schedCol's own, unoffset day-index — used for the
  // within-day remainder, which doesn't care where schedDayList's array starts. wdi/di (the
  // actual array index, schedBackOffset added) is clamped separately for the lookup itself.
  const toDisplayPos = (schedCol: number): number => {
    if (unit === 'weeks') return schedCol;
    if (unit === 'hours') {
      const rawWdi = Math.floor(schedCol / hPerDayExport);
      const wdi = Math.min(Math.max(schedBackOffset + rawWdi, 0), schedDayList.length - 1);
      const hourInDay = schedCol - rawWdi * hPerDayExport;
      const date = schedDayList[wdi];
      const dispIdx = dispDateIndex.get(toDateStr(date)) ?? wdi;
      // showAllHoursExport: each day is 24 columns wide, with the work window sitting at
      // [dayStartHour, dayStartHour+hPerDayExport) within it instead of at [0, hPerDayExport).
      return showAllHoursExport ? dispIdx * 24 + dayStartHour + hourInDay : dispIdx * hPerDayExport + hourInDay;
    }
    const rawDi = Math.floor(schedCol);
    const di = Math.min(Math.max(schedBackOffset + rawDi, 0), schedDayList.length - 1);
    const frac = schedCol - rawDi;
    const date = schedDayList[di];
    const dispIdx = dispDateIndex.get(toDateStr(date)) ?? di;
    return dispIdx + frac;
  };
  // Like toDisplayPos, for a bar's END position: when a schedule end lands exactly at the
  // start of a work day that's preceded by hidden non-working days in display space, snap
  // back to just after the previous work day so bars don't visually bleed through weekends.
  // Mirrors GanttPanel's toDisplayEndPos exactly.
  const toDisplayEndPos = (schedCol: number): number => {
    if (workHoursOnly || unit === 'weeks') return toDisplayPos(schedCol);
    if (unit === 'hours') {
      const rawWdi = Math.floor(schedCol / hPerDayExport);
      const wdi = Math.min(Math.max(schedBackOffset + rawWdi, 0), schedDayList.length - 1);
      const hourInDay = schedCol - rawWdi * hPerDayExport;
      if (hourInDay === 0 && wdi > 0) {
        const prevDispIdx = dispDateIndex.get(toDateStr(schedDayList[wdi - 1])) ?? (wdi - 1);
        const curDispIdx  = dispDateIndex.get(toDateStr(schedDayList[wdi]))     ?? wdi;
        if (curDispIdx > prevDispIdx + 1) {
          return showAllHoursExport ? prevDispIdx * 24 + dayStartHour + hPerDayExport : prevDispIdx * hPerDayExport + hPerDayExport;
        }
      }
      return toDisplayPos(schedCol);
    }
    const rawDi = Math.floor(schedCol);
    const di = Math.min(Math.max(schedBackOffset + rawDi, 0), schedDayList.length - 1);
    const frac = schedCol - rawDi;
    if (frac === 0 && di > 0) {
      const prevDispIdx = dispDateIndex.get(toDateStr(schedDayList[di - 1])) ?? (di - 1);
      const curDispIdx  = dispDateIndex.get(toDateStr(schedDayList[di]))     ?? di;
      if (curDispIdx > prevDispIdx + 1) return prevDispIdx + 1;
    }
    return toDisplayPos(schedCol);
  };
  const maxEndDisplay = Math.ceil(Math.max(toDisplayEndPos(maxEnd), 1));

  // "Set timeline dates" range, in display-column space (same space toDisplayPos produces).
  // viewEndCol is the end date's own column plus one full unit (exclusive boundary, so the
  // end date itself is fully included) — mirrors the live Gantt view's viewEndColIdx.
  // (Resolving it via "the day after end" instead would misfire whenever that next day is a
  // weekend/non-working day, since such days never advance the workday column counter — it
  // would silently exclude the actual end date.) viewStartCol itself is defined earlier
  // (alongside workDays, which its own backward extent depends on) — uses the same
  // array-index-based dateStrToDisplayCol for the same reason (see its own comment).
  const viewEndColRaw = (() => {
    if (!timelineView?.end) return maxEndDisplay;
    const endOwnCol = dateStrToDisplayCol(timelineView.end);
    return endOwnCol + (unit === 'hours' ? displayHoursPerDay : 1);
  })();
  const viewEndCol = Math.min(Math.max(viewEndColRaw, viewStartCol + 1), Math.max(maxEndDisplay, viewStartCol + 1));
  const renderColCount = viewEndCol - viewStartCol;

  // Real (unsnapped) clock end of an is24h task, given its real clock start. task.duration
  // here is calcSchedule's own value (real clock hours), never overwritten the way
  // GanttPanel's adjustedTasks overwrites it, so no extra care is needed to avoid that
  // column-width value. is24hEndFromStart is the single shared implementation (was
  // previously reimplemented separately here, in GanttPanel, and inside lib/gantt.ts
  // itself, twice) — this copy in particular had silently drifted to hardcode Mon-Fri
  // instead of using the flowchart's actual configured work days for is24_5 tasks.
  const is24hRealEnd = (startClock: Date, task: { is24_5?: boolean; duration?: number }): Date =>
    is24hEndFromStart(startClock, task.duration ?? 0, task.is24_5 ?? false, ganttNonWorkingDays, configuredWorkDays);

  // Real-clock Date -> display column, for showAllHoursExport. Mirrors GanttPanel's
  // realDateToDisplayCol, using the workDays display-day list built above.
  const realDateToDisplayCol = (dt: Date): number => {
    const dayNoon = new Date(dt); dayNoon.setHours(12, 0, 0, 0);
    const ty = dayNoon.getFullYear(), tm = dayNoon.getMonth(), td = dayNoon.getDate();
    let di = workDays.length - 1;
    for (let i = 0; i < workDays.length; i++) {
      const d = workDays[i].date;
      if (d.getFullYear() === ty && d.getMonth() === tm && d.getDate() === td) { di = i; break; }
      const dn = new Date(d); dn.setHours(12, 0, 0, 0);
      if (dn > dayNoon) { di = i; break; }
    }
    return di * 24 + dt.getHours() + dt.getMinutes() / 60;
  };

  // A task's display start/end, matching exactly what the bar-rendering loop below
  // computes (is24h real-time override included) — used for the viewport-overlap
  // check just below. Without the override, an is24h task could be excluded (or kept)
  // based on a schedule-column position that disagrees with where its bar actually
  // renders, leaving an included row with no visible bar, or dropping a visible one.
  const taskDispRange = (t: (typeof orderedTasks)[0]): { start: number; end: number } => {
    const is24hReal = showAllHoursExport && (t.alwaysOn || t.is24_5)
      ? is24hRealStarts?.get(t.id)
      : undefined;
    if (is24hReal) return { start: realDateToDisplayCol(is24hReal), end: realDateToDisplayCol(is24hRealEnd(is24hReal, t)) };
    const dispStart = toDisplayPos(t.start);
    const dispEnd = t.duration > 0 ? toDisplayEndPos(t.start + t.duration) : dispStart;
    return { start: dispStart, end: dispEnd };
  };

  // "Only visible bars": drop rows whose bar has no overlap with the timeline
  // range entirely, instead of just clipping their (invisible) bar out of the
  // rendered window — otherwise the export still shows an empty row for them.
  const visibleBarTasks = (onlyVisibleBars && timelineView)
    ? orderedTasks.filter(t => {
        const { start: dispStart, end: dispEnd } = taskDispRange(t);
        return dispEnd >= viewStartCol && dispStart <= viewEndCol;
      })
    : orderedTasks;
  // "Filtered Export": explicit per-row pick, independent of (and combinable with)
  // the timeline-overlap filter above.
  const topLevelExportTasks = selectedBoxIds
    ? (() => { const ids = new Set(selectedBoxIds); return visibleBarTasks.filter(t => ids.has(t.id)); })()
    : visibleBarTasks;

  // Interleave subitem rows and/or group into Area-toplevel rows — mirrors whatever
  // GanttPanel's own View-menu toggles currently show (see useGanttSchedule.ts /
  // GanttPanel.tsx's own call into this same shared function). Everything below
  // this point already treats `tasks` generically (row index only, no hierarchy
  // assumptions), same as GanttPanel's sectionTasks, so subitem/area rows fall out
  // of every existing bar/label/arrow-drawing loop with no further special-casing.
  const areaByBoxIdExport = includeAreaGroupsInExport ? assignBoxesToAreas(boxes, areas) : new Map<number, number>();
  const tasks = (includeSubitemsInExport || includeAreaGroupsInExport || !includeRegularBoxesInExport)
    ? buildExpandedGanttRows<typeof topLevelExportTasks[number]>({
        topLevelTasks: topLevelExportTasks,
        areaByBoxId: areaByBoxIdExport,
        getSubitemsFor: (t) => subitemGanttTasks
          .filter(s => s.parentBoxId === t.id)
          .sort((a, b) => a.start - b.start) as typeof topLevelExportTasks,
        makeAreaHeaderTask: (areaId, members) => {
          const area = areas.find(a => a.id === areaId);
          if (!area) return null;
          // Position each member in DISPLAY-column space, not raw schedule-column
          // space: an is24h (alwaysOn/is24_5) member's schedule column doesn't map
          // linearly to real time once chained — every other bar position in this
          // file already special-cases this (see the is24hReal checks below and
          // taskDispRange above). A naive Math.min/max over raw start/duration would
          // put a straddling is24h member's contribution at the wrong horizontal
          // position (observed live as an Area-toplevel bar rendering shifted onto
          // the next working hour). Stashed as _dispStart/_dispEnd (not start/
          // duration, which stay in schedule-column units so anything that doesn't
          // know about headers still gets a sane, if unused, value) — the bar-drawing
          // loop below reads these directly for any task.id < 0 instead of computing
          // a position from start/duration at all.
          const dispRangeOf = (m: typeof members[number]): { left: number; right: number } => {
            if (showAllHoursExport && (m.alwaysOn || m.is24_5)) {
              const real = is24hRealStarts?.get(m.id);
              if (real) return { left: realDateToDisplayCol(real), right: realDateToDisplayCol(is24hRealEnd(real, m)) };
            }
            const left = toDisplayPos(m.start);
            const right = m.duration > 0 ? toDisplayEndPos(m.start + m.duration) : left;
            return { left, right };
          };
          const ranges = members.map(dispRangeOf);
          const dispStart = Math.min(...ranges.map(r => r.left));
          const dispEnd = Math.max(...ranges.map(r => r.right));
          const start = Math.min(...members.map(m => m.start));
          const end = Math.max(...members.map(m => m.start + m.duration));
          return {
            id: -area.id, x: 0, y: 0, width: 0, height: 0, text: area.title || '', title: area.title || '',
            type: 'regular', departmentColor: area.color, status: 'none', assignee: '', completion: calcAreaCompletion(members),
            importantFinish: false, start, duration: end - start,
            _dispStart: dispStart, _dispEnd: dispEnd,
          } as typeof topLevelExportTasks[number];
        },
        includeSubitems: includeSubitemsInExport,
        includeAreaGroups: includeAreaGroupsInExport,
        includeRegularBoxes: includeRegularBoxesInExport,
      })
    : topLevelExportTasks;

  // Use the live view's exact pixel-per-column value when available — no minimum floor,
  // since the user can already read the screen at this density.
  // Fallback: derive from zoom, with a floor so headers remain legible. showAllHoursExport
  // triples the column count per day (24 vs hPerDayExport) — scale the pixel budget by the
  // same ratio so per-hour density (and thus overall chart width) matches what non-working
  // hours being hidden vs shown should look like, instead of cramming 3x the columns in.
  const widthScaleExport = unit === 'hours' ? displayHoursPerDay / hPerDayExport : 1;
  const dw = screenDw != null
    ? screenDw
    : unit === 'hours' ? Math.max(20, (800 * ganttZoom * widthScaleExport) / maxEndDisplay) : Math.max(6, (800 * ganttZoom) / maxEndDisplay);
  const TIMELINE_W = Math.round(dw * renderColCount);
  const visCount = [visibleLevels.quarters, visibleLevels.months, visibleLevels.weeks, visibleLevels.days, visibleLevels.hours].filter(Boolean).length;
  const HEADER_H = Math.max(visCount, 1) * HDR_H;
  const W = LABEL_W + TIMELINE_W;
  const H = HEADER_H + tasks.length * ROW_H + 4;
  // Every timeline-content x-position below is computed in absolute column space (as before
  // this range restriction existed) and then shifted left by this many pixels so column
  // viewStartCol lands at x=LABEL_W. Content outside [LABEL_W, LABEL_W+TIMELINE_W] is cropped
  // via the gc_timeline clip-path applied to the group that wraps headers/grid/bars/arrows.
  const shiftPx = Math.round(viewStartCol * dw);

  const getCalInfo = (dt: Date) => {
    const m = monthNames[dt.getMonth()];
    const q = `${dt.getFullYear().toString().slice(-2)}Q${Math.floor(dt.getMonth()/3)+1}`;
    const soy = new Date(dt.getFullYear(), 0, 1);
    const wNum = `W${Math.ceil(((dt.getTime()-soy.getTime())/86400000 + soy.getDay()+1)/7)}`;
    return { m, q, wNum };
  };
  const qBands: { label: string; span: number }[] = [];
  const mBands: { label: string; span: number }[] = [];
  const wBands: { label: string; span: number }[] = [];
  const dBands: { day: number; dayName: string; isWeekday: boolean; isNonWorking: boolean; span: number }[] = [];
  const hBands: { label: string; span: number }[] = [];
  let qL: string|null = null, qS = 0, mL: string|null = null, mS = 0, wL: string|null = null, wS = 0;
  for (const wd of workDays) {
    const { m, q, wNum } = getCalInfo(wd.date); const ds = unitsPerWorkDay;
    if (q !== qL) { if (qL) qBands.push({ label: qL, span: qS }); qL = q; qS = ds; } else qS += ds;
    if (m !== mL) { if (mL) mBands.push({ label: mL, span: mS }); mL = m; mS = ds; } else mS += ds;
    if (wNum !== wL) { if (wL) wBands.push({ label: wL, span: wS }); wL = wNum; wS = ds; } else wS += ds;
    dBands.push({ day: wd.date.getDate(), dayName: dayNames[wd.dow], isWeekday: wd.isWeekday, isNonWorking: wd.isNonWorking, span: ds });
    const hS = (fullDaySchedule || showAllHoursExport) ? 0 : dayStartHour, hE = (fullDaySchedule || showAllHoursExport) ? 24 : dayStartHour + hoursPerDay;
    for (let h = hS; h < hE; h++) hBands.push({ label: `${h}:00`, span: unitsPerHour });
  }
  if (qL) qBands.push({ label: qL, span: qS });
  if (mL) mBands.push({ label: mL, span: mS });
  if (wL) wBands.push({ label: wL, span: wS });

  // Helper: convert a date string (YYYY-MM-DD or YYYY-MM-DDTHH:mm) to gantt column offset from ganttStartDate
  // `task` is passed for scheduledStart markers so a time-of-day pin isn't dropped:
  // for an is24h task the real chain/pin-derived clock time is used directly (a work-hour
  // grid column can't represent a pin outside the configured work window, e.g. an
  // overnight is24/5 start); for a regular task the hour is preserved via the same
  // hourOff formula calcStart uses in lib/gantt.ts.
  const ganttExportDateToCol = (dateStr: string, task?: { id: number; alwaysOn?: boolean; is24_5?: boolean }): number | null => {
    const [datePart, timePart] = dateStr.split('T');
    const [yr, mo, dy] = datePart.split('-').map(Number);
    const target = new Date(yr, mo - 1, dy, 12, 0, 0);
    const ganttNoon = new Date(startDate); ganttNoon.setHours(12, 0, 0, 0);
    if (unit === 'weeks') return Math.round((target.getTime() - ganttNoon.getTime()) / (7 * 86400000));
    const isTaskIs24h = !!task && (task.alwaysOn || task.is24_5);
    if (unit === 'hours' && timePart && showAllHoursExport && isTaskIs24h) {
      const real = is24hRealStarts?.get(task!.id);
      if (real) return realDateToDisplayCol(real);
    }
    const td = target.getDate(), tm = target.getMonth(), ty2 = target.getFullYear();
    const dayStartCol = (i: number) => showAllHoursExport ? i * 24 + dayStartHour : i * hPerDayExport;
    const hourOff = (unit === 'hours' && timePart && !isTaskIs24h) ? (() => {
      const [h, mi] = timePart.split(':').map(Number);
      const dayStartH = fullDaySchedule ? 0 : dayStartHour;
      const hpdLocal = fullDaySchedule ? 24 : hoursPerDay;
      return Math.max(0, Math.min(hpdLocal, (h || 0) + (mi || 0) / 60 - dayStartH));
    })() : 0;
    for (let i = 0; i < workDays.length; i++) {
      const wd = workDays[i];
      if (wd.date.getFullYear() === ty2 && wd.date.getMonth() === tm && wd.date.getDate() === td) return unit === 'hours' ? dayStartCol(i) + hourOff : i;
      const wdNoon = new Date(wd.date); wdNoon.setHours(12, 0, 0, 0);
      if (wdNoon > target) return unit === 'hours' ? dayStartCol(i) + hourOff : i;
    }
    return null;
  };

  const taskIdSet = new Set(tasks.map(t => t.id));
  const taskRowIdx = new Map(tasks.map((t, i) => [t.id, i]));

  let svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}">`;
  svg += `<defs>
      <clipPath id="gc_lbl"><rect x="4" y="0" width="${titleLabelW - 8}" height="${H}"/></clipPath>
      <clipPath id="gc_timeline"><rect x="${LABEL_W}" y="0" width="${TIMELINE_W}" height="${H}"/></clipPath>
      <marker id="garrow" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
        <path d="M0,0 L0,6 L8,3 z" fill="#94a3b8"/>
      </marker>
    </defs>`;
  svg += `<rect width="${W}" height="${H}" fill="white"/>`;
  svg += `<rect x="0" y="0" width="${LABEL_W}" height="${H}" fill="#f8faff"/>`;
  if (includeStartEndColumnInExport) {
    svg += `<line x1="${titleLabelW}" y1="0" x2="${titleLabelW}" y2="${H}" stroke="#f1f5f9" stroke-width="1"/>`;
    svg += `<line x1="${titleLabelW + DATE_COL_W}" y1="0" x2="${titleLabelW + DATE_COL_W}" y2="${H}" stroke="#f1f5f9" stroke-width="1"/>`;
  }

  // Header rows. The leftmost label-column cell of each band row is fixed (drawn straight
  // into svg, unshifted); the timeline cells are positioned in absolute column space (as
  // before "set timeline dates" existed) and collected into headerTimelineSvg, which gets
  // shifted + cropped to the view window in one wrapping group below.
  let hY = 0;
  let headerTimelineSvg = '';
  // Band positions are derived from the RUNNING UNROUNDED total, rounding only once
  // per band boundary (x0 = round(sum so far), width = round(sum so far + this span) - x0)
  // rather than accumulating already-rounded per-band widths (`x += Math.round(bw)`).
  // The latter compounds its rounding error once per band — harmless for a handful of
  // week/month bands, but with ~1400 individual hour bands across a multi-month export
  // it drifts the hour row dozens of pixels (multiple real hours) away from both the day
  // row and the bars themselves (which compute position via a single direct
  // multiplication, never accumulated) — this is what made an is24h task's bar appear
  // to sit under the wrong hour tick despite its own x being exactly correct.
  const headerBands = (bands: { label: string; span: number }[], bg: string, tc: string) => {
    svg += `<rect x="0" y="${hY}" width="${LABEL_W}" height="${HDR_H}" fill="${bg}" stroke="#e5e7eb" stroke-width="0.5"/>`;
    let xUnrounded = 0;
    for (const b of bands) {
      const x0 = Math.round(xUnrounded);
      xUnrounded += b.span * dw;
      const bw = Math.round(xUnrounded) - x0;
      headerTimelineSvg += `<rect x="${LABEL_W + x0}" y="${hY}" width="${bw}" height="${HDR_H}" fill="${bg}" stroke="#e5e7eb" stroke-width="0.5"/>`;
      if (bw > 18) headerTimelineSvg += `<text x="${LABEL_W + x0 + 4}" y="${hY + 14}" font-size="10" font-weight="600" fill="${tc}" font-family="sans-serif">${esc(b.label)}</text>`;
    }
    hY += HDR_H;
  };
  if (visibleLevels.quarters) headerBands(qBands, '#dbeafe', '#1e40af');
  if (visibleLevels.months)   headerBands(mBands, '#eff6ff', '#1d4ed8');
  if (visibleLevels.weeks)    headerBands(wBands, '#f8fafc', '#475569');
  if (visibleLevels.days) {
    svg += `<rect x="0" y="${hY}" width="${LABEL_W}" height="${HDR_H}" fill="#f8fafc" stroke="#e5e7eb" stroke-width="0.5"/>`;
    let xUnrounded = 0;
    for (const b of dBands) {
      const x0 = Math.round(xUnrounded);
      xUnrounded += b.span * dw;
      const bw = Math.round(xUnrounded) - x0;
      const bg = b.isNonWorking ? '#fef3c7' : !b.isWeekday ? '#f3f4f6' : '#f8fafc';
      headerTimelineSvg += `<rect x="${LABEL_W + x0}" y="${hY}" width="${bw}" height="${HDR_H}" fill="${bg}" stroke="#e5e7eb" stroke-width="0.5"/>`;
      // Every day band has the SAME true (unrounded) width (b.span is constant,
      // unitsPerWorkDay) — checking the per-band bw here instead of that true width
      // let ±1px rounding noise from the running-total carry (see the comment above
      // this loop) flip individual labels on/off seemingly at random, e.g. "day 3, 6,
      // 8, 11, 14..." with no visible reason. Use the true width, ROUNDED (matching
      // what any one band's own rendered width would round to) so every day's label
      // decision agrees — comparing the raw unrounded value made a width like 15.9
      // (which every band actually renders at 16px) register as narrower than it
      // visually is, hiding labels that used to fit, a regression this exact
      // rounding caused when first introduced.
      if (Math.round(b.span * dw) > 14) {
        headerTimelineSvg += `<text x="${LABEL_W + x0 + bw/2}" y="${hY + 9}" text-anchor="middle" font-size="8" fill="#94a3b8" font-family="sans-serif">${b.dayName}</text>`;
        headerTimelineSvg += `<text x="${LABEL_W + x0 + bw/2}" y="${hY + 18}" text-anchor="middle" font-size="9" fill="#64748b" font-family="sans-serif">${b.day}</text>`;
      }
    }
    hY += HDR_H;
  }
  if (visibleLevels.hours) {
    svg += `<rect x="0" y="${hY}" width="${LABEL_W}" height="${HDR_H}" fill="#f8fafc" stroke="#e5e7eb" stroke-width="0.5"/>`;
    let xUnrounded = 0;
    for (const b of hBands) {
      const x0 = Math.round(xUnrounded);
      xUnrounded += b.span * dw;
      const bw = Math.round(xUnrounded) - x0;
      headerTimelineSvg += `<rect x="${LABEL_W + x0}" y="${hY}" width="${bw}" height="${HDR_H}" fill="#f8fafc" stroke="#e5e7eb" stroke-width="0.5"/>`;
      // Every hour band has the SAME true (unrounded) width (b.span is always
      // unitsPerHour, constant) — using the per-band bw here instead let ±1px
      // rounding noise from the running-total carry (see the comment above this
      // loop) flip individual hour labels on/off seemingly at random (reported as
      // "only 3, 6, 8, 11, 14, 16, 19, 22, 1, 3..." on the axis). Checking the true
      // per-hour width, ROUNDED, makes every hour's label decision agree AND still
      // matches what one band's own rendered width would round to — comparing the
      // raw unrounded value instead hid EVERY hour label whenever the true width
      // sat just under 16 (e.g. 15.9, which every band actually renders at a full
      // 16px), a regression this exact rounding caused when first introduced.
      if (Math.round(b.span * dw) >= 16) headerTimelineSvg += `<text x="${LABEL_W + x0 + 2}" y="${hY + 14}" font-size="8" fill="#94a3b8" font-family="sans-serif">${esc(b.label)}</text>`;
    }
    hY += HDR_H;
  }
  svg += `<g clip-path="url(#gc_timeline)"><g transform="translate(${-shiftPx},0)">${headerTimelineSvg}</g></g>`;

  // Pass 1: row backgrounds + horizontal separators (full width, unshifted — independent of the view window)
  for (let i = 0; i < tasks.length; i++) {
    const ty = HEADER_H + i * ROW_H;
    svg += `<rect x="0" y="${ty}" width="${W}" height="${ROW_H}" fill="${i % 2 === 0 ? 'white' : '#f8fafc'}"/>`;
    svg += `<line x1="${LABEL_W}" y1="${ty}" x2="${LABEL_W}" y2="${ty + ROW_H}" stroke="#e5e7eb" stroke-width="1"/>`;
    svg += `<line x1="0" y1="${ty + ROW_H}" x2="${W}" y2="${ty + ROW_H}" stroke="#f1f5f9" stroke-width="0.5"/>`;
  }

  // Vertical grid lines at finest active level, and Pass 2 task content (bars/markers) — both
  // positioned in absolute column space and collected for the shifted+cropped group below.
  let timelineSvg = '';
  {
    // Same running-unrounded-total technique as the header bands above — avoids
    // compounding per-band rounding error over many grid-line steps (especially the
    // hour level, ~1400 steps for a multi-month export) so these lines land exactly
    // where the header bands (and the bars themselves) say they should.
    let xUnrounded = 0;
    if (visibleLevels.hours) {
      for (const b of hBands) {
        const x0 = Math.round(xUnrounded);
        xUnrounded += b.span * dw;
        const xEnd = Math.round(xUnrounded);
        if (showAllHoursExport) {
          const h = parseInt(b.label, 10);
          if (h < dayStartHour || h >= dayStartHour + hoursPerDay) {
            timelineSvg += `<rect x="${LABEL_W + x0}" y="${HEADER_H}" width="${xEnd - x0}" height="${H - HEADER_H}" fill="#f3f4f6" fill-opacity="0.7"/>`;
          }
        }
        timelineSvg += `<line x1="${LABEL_W + xEnd}" y1="${HEADER_H}" x2="${LABEL_W + xEnd}" y2="${H}" stroke="#e5e7eb" stroke-width="1"/>`;
      }
    } else if (visibleLevels.days) {
      for (const b of dBands) {
        const x0 = Math.round(xUnrounded);
        xUnrounded += b.span * dw;
        const xEnd = Math.round(xUnrounded);
        if (!b.isWeekday || b.isNonWorking) timelineSvg += `<rect x="${LABEL_W + x0}" y="${HEADER_H}" width="${xEnd - x0}" height="${H - HEADER_H}" fill="#f3f4f6" fill-opacity="0.7"/>`;
        timelineSvg += `<line x1="${LABEL_W + xEnd}" y1="${HEADER_H}" x2="${LABEL_W + xEnd}" y2="${H}" stroke="#e5e7eb" stroke-width="1"/>`;
      }
    } else if (visibleLevels.weeks) {
      for (const b of wBands) { xUnrounded += b.span * dw; const xEnd = Math.round(xUnrounded); timelineSvg += `<line x1="${LABEL_W + xEnd}" y1="${HEADER_H}" x2="${LABEL_W + xEnd}" y2="${H}" stroke="#e2e8f0" stroke-width="1"/>`; }
    } else if (visibleLevels.months) {
      for (const b of mBands) { xUnrounded += b.span * dw; const xEnd = Math.round(xUnrounded); timelineSvg += `<line x1="${LABEL_W + xEnd}" y1="${HEADER_H}" x2="${LABEL_W + xEnd}" y2="${H}" stroke="#d1d5db" stroke-width="1"/>`; }
    } else if (visibleLevels.quarters) {
      for (const b of qBands) { xUnrounded += b.span * dw; const xEnd = Math.round(xUnrounded); timelineSvg += `<line x1="${LABEL_W + xEnd}" y1="${HEADER_H}" x2="${LABEL_W + xEnd}" y2="${H}" stroke="#d1d5db" stroke-width="1"/>`; }
    }
  }

  const durSuffix = ganttDurationDisplay === 'hours' ? 'h' : ganttDurationDisplay === 'weeks' ? 'w' : 'd';
  for (let i = 0; i < tasks.length; i++) {
    const task = tasks[i];
    const ty = HEADER_H + i * ROW_H;

    // Status dot (unshifted — lives in the fixed label column)
    const dotColor = task.status === 'done' ? '#10b981' : task.status === 'in-progress' ? '#3b82f6' : task.status === 'todo' ? '#f59e0b' : '#d1d5db';
    svg += `<circle cx="12" cy="${ty + ROW_H/2}" r="4" fill="${dotColor}"/>`;

    // Label (unshifted). Truncated to a character-count estimate of LABEL_W (not just
    // left to the clip-path below) — this is also the resource's own NAME for a
    // resource-summary row (GanttPanel.tsx builds those as title-only pseudo-tasks),
    // and a long one visually ran rightward past the label column, over the first
    // bar's own on-bar duration text. The live view already truncates the equivalent
    // label with CSS text-overflow:ellipsis (reliable, since the browser's own layout
    // engine measures it); clip-path on an SVG <text> is NOT reliably respected by
    // every PNG-rasterization path, so this truncates the STRING itself instead of
    // depending on clipping to hide the overflow.
    const lblRaw = (task as any).title || task.text || '';
    const lblMaxChars = Math.max(3, Math.floor((titleLabelW - 24 - 8) / 6.5));
    const lbl = lblRaw.length > lblMaxChars ? lblRaw.slice(0, lblMaxChars - 1) + '…' : lblRaw;
    svg += `<text x="24" y="${ty + ROW_H/2 + 4}" font-size="12" fill="#374151" font-family="sans-serif" clip-path="url(#gc_lbl)">${esc(lbl)}</text>`;

    // "Show start/end" columns: same text GanttPanel.tsx's own cells show, in the
    // two added strips between titleLabelW and LABEL_W (Start, then End). Empty for
    // a task with no entry (e.g. a subitem, an Area header, a resource-summary
    // pseudo-row) — same "no single real box id" cases the live view leaves blank.
    if (includeStartEndColumnInExport) {
      const se = startEndText?.get(task.id);
      if (se) {
        const seMaxChars = Math.max(3, Math.floor((DATE_COL_W - 8) / 6));
        const trunc = (s: string) => s.length > seMaxChars ? s.slice(0, seMaxChars - 1) + '…' : s;
        svg += `<text x="${titleLabelW + 4}" y="${ty + ROW_H/2 + 4}" font-size="10" fill="#6b7280" font-family="sans-serif">${esc(trunc(se.start))}</text>`;
        svg += `<text x="${titleLabelW + DATE_COL_W + 4}" y="${ty + ROW_H/2 + 4}" font-size="10" fill="#6b7280" font-family="sans-serif">${esc(trunc(se.end))}</text>`;
      }
    }

    // Bar — positions converted from schedule space (task.start/duration, always computed by
    // calcSchedule as if only weekdays exist) to display space (this grid, which spreads out
    // over weekends too when workHoursOnly is off).
    if (task.duration >= 0) {
      // Area-toplevel row (task.id < 0, see buildExpandedGanttRows' makeAreaHeaderTask
      // callback above): its display range was already resolved (correctly handling
      // any is24h member) at construction time and stashed on _dispStart/_dispEnd —
      // task.start/duration are schedule-column values for every OTHER task here, but
      // never meaningful for a header, so skip straight to those instead.
      const headerDisp = task.id < 0 ? (task as any)._dispStart !== undefined
        ? { left: (task as any)._dispStart as number, right: (task as any)._dispEnd as number }
        : null : null;
      // showAllHoursExport: an is24h task's schedule column doesn't linearly map to real
      // time once chained, so its bar is placed from its actual real start/end instead of
      // the schedule-column-based toDisplayPos (same reasoning as GanttPanel's getEndCol).
      const is24hReal = !headerDisp && showAllHoursExport && (task.alwaysOn || task.is24_5)
        ? is24hRealStarts?.get(task.id)
        : undefined;
      const dispStart = headerDisp ? headerDisp.left : is24hReal ? realDateToDisplayCol(is24hReal) : toDisplayPos(task.start);
      const dispEnd = headerDisp ? headerDisp.right : is24hReal
        ? realDateToDisplayCol(is24hRealEnd(is24hReal, task))
        : (task.duration > 0 ? toDisplayEndPos(task.start + task.duration) : dispStart);
      // Split at night gaps for regular (non-is24h) tasks when non-working hours are
      // visible — otherwise a task spanning into the next work day renders as one solid
      // bar bleeding through the night. Mirrors GanttPanel's shouldSplitHours. Never for
      // a header — its range is already fully resolved above.
      const segs: Array<{ start: number; end: number }> = [];
      if (!headerDisp && !is24hReal && showAllHoursExport && task.duration > 0) {
        const endSched = task.start + task.duration;
        let segStart = task.start;
        let boundary = Math.floor(segStart / hPerDayExport) * hPerDayExport + hPerDayExport;
        const nightGap = 24 - hPerDayExport;
        while (boundary < endSched) {
          segs.push({ start: toDisplayPos(segStart), end: toDisplayPos(boundary) - nightGap });
          segStart = boundary;
          boundary += hPerDayExport;
        }
        segs.push({ start: toDisplayPos(segStart), end: dispEnd });
      } else {
        segs.push({ start: dispStart, end: dispEnd });
      }

      const by = ty + 5; const bh = ROW_H - 10;
      const totalDispW = segs.reduce((s, sg) => s + Math.max(sg.end - sg.start, 0), 0);
      const displayDuration = Math.round(convertDurationUnit(task.duration, unit, ganttDurationDisplay, hoursPerDay) * 100) / 100;
      const barLabel = `${displayDuration}${durSuffix}${task.completion > 0 ? ` (${task.completion}%)` : ''}`;
      let lastBx = 0, lastBw = 0;
      for (let si = 0; si < segs.length; si++) {
        const seg = segs[si];
        const segW = Math.max(seg.end - seg.start, 0);
        const bx = LABEL_W + Math.round(seg.start * dw);
        // Minimum width so a zero-duration (milestone) task still renders a visible
        // sliver instead of nothing, matching the live view's mini-bar treatment.
        const bw = Math.max(4, Math.round(segW * dw));
        timelineSvg += `<rect x="${bx}" y="${by}" width="${bw}" height="${bh}" rx="3" fill="${task.departmentColor || '#93c5fd'}"/>`;
        if (task.completion > 0 && totalDispW > 0) {
          const prevW = segs.slice(0, si).reduce((s, sg) => s + Math.max(sg.end - sg.start, 0), 0);
          const segStartFrac = prevW / totalDispW, segEndFrac = (prevW + segW) / totalDispW;
          const fillEnd = Math.min(task.completion / 100, segEndFrac);
          if (fillEnd > segStartFrac) {
            const segCompPct = (fillEnd - segStartFrac) / (segEndFrac - segStartFrac) * 100;
            const fw = Math.round(bw * segCompPct / 100);
            timelineSvg += `<rect x="${bx}" y="${by}" width="${fw}" height="${bh}" rx="3" fill="#000000" fill-opacity="0.18"/>`;
            if (segCompPct < 100) timelineSvg += `<line x1="${bx + fw}" y1="${by}" x2="${bx + fw}" y2="${by + bh}" stroke="#000000" stroke-opacity="0.3" stroke-width="2"/>`;
          }
        }
        // Duration text on the first segment only — centered when wide enough, otherwise
        // placed just to the right so short and zero-duration bars stay legible.
        if (si === 0) {
          if (bw > 24) {
            timelineSvg += `<text x="${bx + bw/2}" y="${by + bh/2 + 4}" text-anchor="middle" font-size="10" font-weight="600" fill="#1f2937" font-family="sans-serif">${esc(barLabel)}</text>`;
          } else {
            // Leave room for the importantFinish diamond (drawn at lastBx+lastBw+5, ~10px wide) so the label doesn't overlap it.
            const outsideX = bx + bw + (task.importantFinish && segs.length === 1 ? 16 : 4);
            timelineSvg += `<text x="${outsideX}" y="${by + bh/2 + 4}" font-size="10" font-weight="600" fill="#1f2937" font-family="sans-serif">${esc(barLabel)}</text>`;
          }
        }
        lastBx = bx; lastBw = bw;
      }
      if (task.importantFinish) {
        const mx = lastBx + lastBw + 5; const my = ty + ROW_H / 2;
        timelineSvg += `<polygon points="${mx},${my-5} ${mx+5},${my} ${mx},${my+5} ${mx-5},${my}" fill="#f59e0b"/>`;
      }
    }

    // Scheduled start (set date) — dashed dark-blue line
    if ((task as any).scheduledStart) {
      const col = ganttExportDateToCol((task as any).scheduledStart, task as any);
      if (col !== null && col >= 0) {
        const lx = LABEL_W + Math.round(col * dw);
        timelineSvg += `<line x1="${lx}" y1="${ty}" x2="${lx}" y2="${ty + ROW_H}" stroke="#1e3a8a" stroke-width="2" stroke-dasharray="4,4"/>`;
        timelineSvg += `<text x="${lx + 2}" y="${ty + 10}" font-size="8" fill="#1e3a8a" font-family="sans-serif">${esc((task as any).scheduledStart.split('T')[0])}</text>`;
      }
    }
    // Earliest start — dashed blue line (date format only, not week format)
    if ((task as any).earliestStart && !(task as any).earliestStart.startsWith('W')) {
      const col = ganttExportDateToCol((task as any).earliestStart);
      if (col !== null && col > 0) {
        const lx = LABEL_W + Math.round(col * dw);
        timelineSvg += `<line x1="${lx}" y1="${ty}" x2="${lx}" y2="${ty + ROW_H}" stroke="#3b82f6" stroke-width="2" stroke-dasharray="4,4"/>`;
        timelineSvg += `<text x="${lx + 2}" y="${ty + 10}" font-size="8" fill="#3b82f6" font-family="sans-serif">${esc((task as any).earliestStart.split('T')[0])}</text>`;
      }
    }
  }

  // Dependency arrows — only when enabled in the view
  if (showAllGanttDeps) {
    const makePath = (x1: number, y1: number, x2: number, y2: number) => {
      const cx = Math.max(40, Math.abs(x2 - x1) / 2);
      return `M ${x1} ${y1} C ${x1 + cx} ${y1} ${x2 - cx} ${y2} ${x2} ${y2}`;
    };
    for (const a of (arrows as any[])) {
      const fromId = a.fromBox ?? a.from;
      const toId = a.toBox ?? a.to;
      if (!taskIdSet.has(fromId) || !taskIdSet.has(toId)) continue;
      const fi = taskRowIdx.get(fromId)!; const ti = taskRowIdx.get(toId)!;
      const ft = tasks[fi]; const tt = tasks[ti];
      if (!ft || !tt || ft.duration <= 0) continue;
      // showAllHoursExport: anchor to the real start/end, same as the bar itself —
      // see taskDispRange above for why the plain schedule-column position is wrong
      // for a chained is24h task.
      const x1 = LABEL_W + Math.round(taskDispRange(ft).end * dw) - 4;
      const y1 = HEADER_H + fi * ROW_H + ROW_H / 2;
      const x2 = LABEL_W + Math.round(taskDispRange(tt).start * dw);
      const y2 = HEADER_H + ti * ROW_H + ROW_H / 2;
      timelineSvg += `<path d="${makePath(x1, y1, x2, y2)}" fill="none" stroke="#94a3b8" stroke-width="1.5" marker-end="url(#garrow)"/>`;
    }
  }

  // Bars, grid lines, markers and arrows all live in the same absolute column space (unchanged
  // from before "set timeline dates" existed) — shift left so viewStartCol lands at x=LABEL_W,
  // and crop to the view window. Without a timelineView, viewStartCol is 0 and viewEndCol is
  // maxEnd, so this is a no-op shift with a clip that exactly matches the unrestricted content.
  svg += `<g clip-path="url(#gc_timeline)"><g transform="translate(${-shiftPx},0)">${timelineSvg}</g></g>`;

  svg += `</svg>`;
  return { svg, W, H };
}

// ── Flowchart canvas → SVG ────────────────────────────────────────────────────
// FlowchartSVGOptions is defined in FlowchartSvgScene.tsx and re-exported above.

export function buildExportSVGString(opts: FlowchartSVGOptions): { svg: string; W: number; H: number } | null {
  if (opts.boxes.length === 0) return null;
  const { W, totalH } = computeSvgDimensions(opts);
  const svg = renderToStaticMarkup(createElement(FlowchartSvgScene, opts));
  return { svg, W, H: totalH };
}
